import { css } from '@emotion/react';
import { ToucanComponents, ToucanColors } from '@jointoucan/toucan-design';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import useMediaQuery from '@mui/material/useMediaQuery';
import { GetServerSideProps } from 'next';
import { useTranslation } from 'next-i18next';

import { useUILanguage } from '~/components/LocaleProvider';
import { SeoOverrides } from '~/components/Seo/SeoOverrides';
import Divider from '~/components/shared/Divider';
import Layout from '~/components/shared/Layout';
import { buildPolicyUrl } from '~/constants/urls';
import { getTranslations } from '~/lib/next-i18next';

const { Typography } = ToucanComponents;

const ExtensionTerms = () => {
  const theme = useTheme();
  const { t } = useTranslation('tos');
  const isViewportAboveSm = useMediaQuery(theme.breakpoints.up('sm'));
  const { uiLanguage: sourceLanguage } = useUILanguage();
  const policyUrl = buildPolicyUrl(sourceLanguage);

  const pageDescription = t('tosMetaDescription');
  return (
    <>
      <SeoOverrides
        title={t('tosMetaTitle')}
        description={pageDescription}
        openGraph={{
          title: t('tosMetaTitle'),
          description: pageDescription,
        }}
      />
      <Layout isContained={false}>
        <Box
          display="flex"
          justifyContent="center"
          css={css`
            width: 100%;
          `}
          pb={10}
        >
          <Box
            display="flex"
            flexDirection="column"
            mt={10}
            mx={2}
            css={css`
              max-width: 684px;
            `}
          >
            <Typography
              variant={isViewportAboveSm ? 'xl' : 'lg'}
              font="secondary"
              isBold
              color={ToucanColors.gray[600]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              TERMS OF USE
            </Typography>
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              Last Revised on December 15, 2023
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              These Terms of Use (these “Terms”) set forth the terms and conditions that apply to your access and use of
              (i) the websites (including the website located at www.jointoucan.com (the “Website”)), (ii) software
              applications and other online services, together with any content, tools, features and functionality
              offered on or through such websites, software applications and other online services, including the Toucan
              extension for web browsers (the “Extension), and (iii) the mobile applications and related technologies
              (the “Mobile Apps” and together with the foregoing items in clauses (i) and (ii), the “Services”) provided
              by Babbel GmbH. (“Company”, “we” or “us”).
              <br />
              <br />
              These Terms govern your access to and use of the Services. Please read these Terms carefully, as they
              include important information about your legal rights. By accessing and/or using the Services, you are
              agreeing to these Terms. If you do not understand or agree to these Terms, please do not use the Services.{' '}
              <br />
              <br />
              For purposes of these Terms, “you” and “your” means you as the user of the Services. If you use the
              Services on behalf of a company or other entity then “you” includes you and that entity, and you represent
              and warrant that (a) you are an authorized representative of the entity with the authority to bind the
              entity to these Terms, and (b) you agree to these Terms on the entity’s behalf. <br />
              <br />
              <strong>
                Please note that Section 9 contains an arbitration clause and class action waiver. By agreeing to these
                Terms, you agree (a) to resolve all disputes with us through binding individual arbitration, which means
                that you waive any right to have those disputes decided by a judge or jury, and (b) that you waive your
                right to participate in class actions, class arbitrations, or representative actions. You have the right
                to opt-out of arbitration as explained in Section 9.
              </strong>
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              1. WHO MAY USE THE SERVICES
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              You must be 13 years of age or older and reside in the United States or any of its territories or a
              country that is not subject to a U.S. Government embargo, or that has not been designated by the U.S.
              Government as a “terrorist supporting” country to use the Services. Minors under the age of majority in
              their jurisdiction but that are at least 13 years of age are only permitted to use the Services if the
              minor’s parent or guardian accepts these Terms on the minor’s behalf prior to use of the Services.
              Children under the age of 13 are not permitted to use the Services. By using the Services, you represent
              and warrant that you meet these requirements.
              <br />
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              2. USER ACCOUNTS
            </Typography>
            <ol
              css={css`
                padding: 0;
                margin-top: ${theme.spacing(1)};
                margin-bottom: ${theme.spacing(1)};
                margin-left: ${theme.spacing(2)};
              `}
            >
              <li>
                Creating and Safeguarding your Account. To use certain of the Services, you may need to create an
                account (“Account”). You agree to provide us with accurate, complete and updated information for your
                Account. You can access, edit and update the information on your Account via the user account page of
                the Extension, Website or Mobile Apps. You are solely responsible for any activity on your Account and
                for maintaining the confidentiality and security of your password. We are not liable for any acts or
                omissions by you in connection with your Account. You must immediately notify us at toucan@babbel.com
                you know or have any reason to suspect that your Account or password have been stolen, misappropriated
                or otherwise compromised, or in case of any actual or suspected unauthorized use of your Account.
              </li>
            </ol>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              3. LOCATION OF OUR PRIVACY POLICY
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              <ol
                css={css`
                  padding: 0;
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: ${theme.spacing(1)};
                  margin-left: ${theme.spacing(2)};
                `}
              >
                <li>
                  Privacy Policy. Our Privacy Policy describes how we handle the information you provide to us when you
                  use the Services. For an explanation of our privacy practices, please visit our Privacy Policy located
                  at {policyUrl}.
                </li>
              </ol>
              <br />
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              4. RIGHTS WE GRANT YOU
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              <ol
                css={css`
                  padding: 0;
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: ${theme.spacing(1)};
                  margin-left: ${theme.spacing(2)};
                `}
              >
                <li>
                  License Grant. Subject to your compliance with these Terms, the Company hereby grants to you, a
                  personal, worldwide, royalty-free, non-assignable, non-sublicensable, non-transferrable, and
                  non-exclusive license to use the software provided to you as part of the Services (and to download a
                  single copy of the Extension onto the equipment or device specified by us for your personal use). This
                  license has the sole purpose of enabling you to use and enjoy the benefit of the Services as provided
                  by us, in the manner permitted by these Terms and subject to the use restrictions described below.
                  Your access and use of the Services may be interrupted from time to time for any of several reasons,
                  including, without limitation, the malfunction of equipment, periodic updating, maintenance or repair
                  of the Service or other actions that Company, in its sole discretion, may elect to take.
                </li>
                <li>
                  Restrictions On Your Use of the Services. You may not do any of the following, unless applicable laws
                  or regulations prohibit these restrictions or you have our written permission to do so:{' '}
                  <ol
                    css={css`
                      list-style-type: lower-alpha;
                    `}
                  >
                    <li>
                      download, store, modify, copy, distribute, transmit, display, perform, reproduce, duplicate,
                      publish, license, create derivative works from, or offer for sale any information or content
                      contained on, or obtained from or through, the Services;
                    </li>
                    <li>
                      duplicate, decompile, reverse engineer, disassemble or decode the Services (including any
                      underlying idea or algorithm), or attempt to do any of the same;
                    </li>
                    <li>
                      use, reproduce or remove any copyright, trademark, service mark, trade name, slogan, logo, image,
                      or other proprietary notation displayed on or through the Services;
                    </li>
                    <li>
                      use automation software (bots), hacks, modifications (mods) or any other unauthorized third-party
                      software designed to modify the Services;
                    </li>
                    <li>
                      exploit the Services for any commercial purpose, including without limitation communicating or
                      facilitating any commercial advertisement or solicitation;
                    </li>
                    <li>
                      access or use the Services in any manner that could disable, overburden, damage, disrupt or impair
                      the Services or interfere with any other party’s access to or use of the Services or use any
                      device, software or routine that causes the same, including by running any Maillist, Listserv, any
                      form of auto-responder or “spam” on the Services, or any processes that run or are activated while
                      you are not logged into the Services, or that otherwise interfere with the proper working of the
                      Services or that would bypass the navigational structure or presentation of the Service;
                    </li>
                    <li>
                      attempt to gain unauthorized access to, interfere with, damage or disrupt the Services, accounts
                      registered to other users (such as attempting, in any manner, to obtain the password, account, or
                      other security information of any other user), the computer systems or networks connected to the
                      Services, or any passwords or security encryption codes;
                    </li>
                    <li>
                      circumvent, remove, alter, deactivate, degrade or thwart any technological measure or content
                      protections of the Services;
                    </li>
                    <li>
                      jeopardize the security of Your Account or any other user’s account (such as by allowing someone
                      who is not you to log in to the Services as you);
                    </li>
                    <li>
                      use any robot, spider, crawlers or other automatic device, process, software or queries that
                      intercepts, “mines,” scrapes or otherwise accesses the Services to monitor, extract, copy or
                      collect information or data from or through the Services, or engage in any manual process to do
                      the same;
                    </li>
                    <li>
                      introduce any viruses, trojan horses, worms, logic bombs or other materials that are malicious or
                      technologically harmful into our systems;
                    </li>
                    <li>
                      use the Services for illegal, harassing, unethical, or disruptive purposes, or otherwise use the
                      Services in a manner that is harmful, fraudulent, deceptive, threatening, defamatory, obscene or
                      otherwise objectionable;
                    </li>
                    <li>
                      infringe or violate the intellectual property rights or any other rights of anyone (including the
                      Company) in connection with your use of the Services;
                    </li>
                    <li>
                      violate any applicable law or regulation in connection with your access to or use of the Services;
                      or
                    </li>
                    <li>access or use the Services in any way not expressly permitted by these Terms.</li>
                  </ol>
                </li>
              </ol>
              <br />
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              5. OWNERSHIP
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              <ol
                css={css`
                  padding: 0;
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: ${theme.spacing(1)};
                  margin-left: ${theme.spacing(2)};
                `}
              >
                <li>
                  Ownership of the Services. The Services, including their &quot;look and feel&quot; (e.g., text,
                  graphics, images, logos), proprietary content, information and other materials, are protected under
                  copyright, trademark and other intellectual property laws. You agree that the Company and/or its
                  licensors own all right, title and interest in and to the Services (including any and all intellectual
                  property rights therein) and you agree not to take any action(s) inconsistent with such ownership
                  interests. We and our licensors reserve all rights in connection with the Services and its content,
                  including, without limitation, the exclusive right to create derivative works.
                </li>
                <li>
                  Ownership of Trademarks. The Company’s name, the Company’s trademarks, the Company’s logo and all
                  related names, logos, product and service names, designs and slogans are trademarks of the Company or
                  its affiliates or licensors. Other names, logos, product and service names, designs and slogans that
                  appear on the Services are the property of their respective owners, who may or may not be affiliated
                  with, connected to, or sponsored by us.
                </li>
                <li>
                  Ownership of Feedback. We welcome feedback, comments and suggestions for improvements to the Services
                  (“Feedback”). You acknowledge and expressly agree that any contribution of Feedback does not and will
                  not give or grant you any right, title or interest in the Services or in any such Feedback. All
                  Feedback becomes the sole and exclusive property of the Company, and the Company may use and disclose
                  Feedback in any manner and for any purpose whatsoever without further notice or compensation to you
                  and without retention by you of any proprietary or other right or claim. You hereby assign to the
                  Company any and all right, title and interest (including, but not limited to, any patent, copyright,
                  trade secret, trademark, show-how, know-how, moral rights and any and all other intellectual property
                  right) that you may have in and to any and all Feedback.
                </li>
              </ol>
              <br />
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              6. MOBILE SERVICES AND SOFTWARE
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              <ol
                css={css`
                  padding: 0;
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: ${theme.spacing(1)};
                  margin-left: ${theme.spacing(2)};
                `}
              >
                <li>
                  Mobile Services. The Services include certain services that are available via a mobile device,
                  including (i) the ability to upload content to the Services via a mobile device, (ii) the ability to
                  browse the Services and the Website from a mobile device, and (iii) the ability to access certain
                  features and content through Mobile Apps (collectively, the “Mobile Services”). To the extent you
                  access the Services through a mobile device, your wireless service carrier’s standard charges, data
                  rates, and other fees may apply. In addition, downloading, installing, or using certain Mobile
                  Services may be prohibited or restricted by your carrier, and not all Mobile Services may work with
                  all carriers or devices.
                </li>
                <li>
                  Mobile App License. Subject to these Terms, Company hereby grants to you a limited, revocable,
                  non-exclusive, non-transferable, non-sublicensable license to (a) install the Mobile App on one mobile
                  device and (b) use the Mobile App for your own personal use solely to access and use the Services. For
                  clarity, the foregoing is not intended to prohibit you from installing the Mobile App on another
                  device on which you also agreed to these Terms. Each instance of these Terms that you agree to in
                  connection with downloading a Mobile App grants you the aforementioned rights in connection with the
                  installation and use of the Mobile App on one device.
                </li>
                <li>
                  Third-Party Distribution Channels. Company offers software that may be made available through the
                  Apple App Store, the Google Play Store, or other distribution channels (“Distribution Channels”). If
                  you obtain such software through a Distribution Channel, you may be subject to additional terms of the
                  Distribution Channel. These Terms are between you and us only, and not with the Distribution Channel.
                  To the extent that you utilize any other third-party products and services in connection with your use
                  of the Services, you agree to comply with all applicable terms of any agreement for such third-party
                  products and services.
                </li>
                <li>
                  Apple-Enabled Software. With respect to Mobile Apps that are made available for your use in connection
                  with an Apple-branded product (the, “Apple-Enabled Software”), in addition to the other terms and
                  conditions set forth in these Terms, the following terms and conditions apply:
                  <ol
                    css={css`
                      list-style-type: lower-alpha;
                    `}
                  >
                    <li>
                      Company and you acknowledge that these Terms are concluded between Company and you only, and not
                      with Apple Inc. (“Apple”), and that as between Company and Apple, Company, not Apple, is solely
                      responsible for the Apple-Enabled Software and the content thereof.
                    </li>
                    <li>
                      You may not use the Apple-Enabled Software in any manner that is in violation of or inconsistent
                      with the Usage Rules set forth for Apple-Enabled Software in, or otherwise be in conflict with,
                      the Apple Media Services Terms and Conditions.
                    </li>
                    <li>
                      Your license to use the Apple-Enabled Software is limited to a non-transferable license to use the
                      Apple-Enabled Software on an iOS product that you own or control, as permitted by the “Usage
                      Rules” set forth in the Apple Media Services Terms and Conditions, except that such Apple-Enabled
                      Software may be accessed and used by other accounts associated with the purchaser via Apple’s
                      Family Sharing or volume purchasing programs.
                    </li>
                    <li>
                      Apple has no obligation whatsoever to provide any maintenance or support services with respect to
                      the Apple-Enabled Software.
                    </li>
                    <li>
                      Apple is not responsible for any product warranties, whether express or implied by law. In the
                      event of any failure of the Apple-Enabled Software to conform to any applicable warranty, you may
                      notify Apple, and Apple will refund the purchase price for the Apple-Enabled Software, if any, to
                      you; and, to the maximum extent permitted by applicable law, Apple will have no other warranty
                      obligation whatsoever with respect to the Apple-Enabled Software, or any other claims, losses,
                      liabilities, damages, costs, or expenses attributable to any failure to conform to any warranty,
                      which will be Company’s sole responsibility, to the extent it cannot be disclaimed under
                      applicable law.
                    </li>
                    <li>
                      Company and you acknowledge that Company, not Apple, is responsible for addressing any claims of
                      you or any third party relating to the Apple-Enabled Software or your possession and/or use of
                      that Apple-Enabled Software, including: (i) product liability claims; (ii) any claim that the
                      Apple-Enabled Software fails to conform to any applicable legal or regulatory requirement; and
                      (iii) claims arising under consumer protection, privacy, or similar legislation.
                    </li>
                    <li>
                      In the event of any third-party claim that the Apple-Enabled Software or your possession and use
                      of that Apple-Enabled Software infringes that third party’s intellectual property rights, as
                      between Company and Apple, Company, not Apple, will be solely responsible for the investigation,
                      defense, settlement, and discharge of any such intellectual property infringement claim.
                    </li>
                    <li>
                      You represent and warrant that (i) you are not located in a country that is subject to a U.S.
                      Government embargo, or that has been designated by the U.S. Government as a “terrorist supporting”
                      country; and (ii) you are not listed on any U.S. Government list of prohibited or restricted
                      parties.
                    </li>
                    <li>
                      If you have any questions, complaints, or claims with respect to the Apple-Enabled Software, they
                      should be directed to Company as follows:
                      <br /> <br />
                      toucan@babbel.com
                      <br />
                      <br />
                      Andreasstraße 72, 10243 Berlin, Germany.
                    </li>
                    <li>
                      You must comply with applicable third-party terms of agreement when using the Apple-Enabled
                      Software, e.g., your wireless data service agreement.
                    </li>
                    <li>
                      Company and you acknowledge and agree that Apple, and Apple’s subsidiaries, are third-party
                      beneficiaries of these Terms with respect to the Apple-Enabled Software, and that, upon your
                      acceptance of the terms and conditions of these Terms, Apple will have the right (and will be
                      deemed to have accepted the right) to enforce these Terms against you with respect to the
                      Apple-Enabled Software as a third-party beneficiary thereof.
                    </li>
                  </ol>
                </li>
              </ol>
              <br />
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              7. THIRD PARTY SERVICES AND MATERIALS
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              <ol
                css={css`
                  padding: 0;
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: ${theme.spacing(1)};
                  margin-left: ${theme.spacing(2)};
                `}
              >
                <li>
                  Use of Third Party Materials in the Services. Certain Services may display, include or make available
                  content, data, information, applications or materials from third parties (“Third Party Materials”) or
                  provide links to certain third party websites. By using the Services, you acknowledge and agree that
                  the Company is not responsible for examining or evaluating the content, accuracy, completeness,
                  availability, timeliness, validity, copyright compliance, legality, decency, quality or any other
                  aspect of such Third Party Materials or websites. We do not warrant or endorse and do not assume and
                  will not have any liability or responsibility to you or any other person for any third-party services,
                  Third Party Materials or third-party websites, or for any other materials, products, or services of
                  third parties. Third Party Materials and links to other websites are provided solely as a convenience
                  to you.
                </li>
              </ol>
              <br />
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              8. DISCLAIMERS, LIMITATIONS OF LIABILITY AND INDEMNIFICATION
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              <ol
                css={css`
                  padding: 0;
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: ${theme.spacing(1)};
                  margin-left: ${theme.spacing(2)};
                `}
              >
                <li>
                  Disclaimers. Your access to and use of the Services, are at your own risk. You understand and agree
                  that the Services are provided to you on an “AS IS” and “AS AVAILABLE” basis. Without limiting the
                  foregoing, to the maximum extent permitted under applicable law, the Company, its parents, affiliates,
                  related companies, officers, directors, employees, agents, representatives, partners and licensors
                  (the “the Company Entities”) DISCLAIM ALL WARRANTIES AND CONDITIONS, WHETHER EXPRESS OR IMPLIED,
                  INCLUDING WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, FREEDOM FROM DEFECTS,
                  NON-INFRINGEMENT AND ALL WARRANTIES IMPLIED FROM ANY COURSE OF DEALING OR USAGE OF TRADE. The Company
                  Entities make no warranty or representation and disclaim all responsibility and liability for: (a) the
                  completeness, accuracy, availability, timeliness, security or reliability of the Services; (b) any
                  harm to your computer system, loss of data, or other harm that results from your access to or use of
                  the Services; (c) the operation or compatibility with any other application or any particular system
                  or device; (d) whether the Services will meet your requirements or be available on an uninterrupted,
                  secure or error-free basis; and (e) the deletion of, or the failure to store or transmit,
                  communications maintained by the Services. No advice or information, whether oral or written, obtained
                  from the Company Entities or through the Services, will create any warranty or representation not
                  expressly made herein.
                </li>
                <li>
                  Limitations of Liability. TO THE EXTENT NOT PROHIBITED BY LAW, YOU AGREE THAT IN NO EVENT WILL THE
                  COMPANY ENTITIES BE LIABLE (A) FOR DAMAGES OF ANY KIND, INCLUDING DIRECT, INDIRECT, SPECIAL,
                  EXEMPLARY, INCIDENTAL, CONSEQUENTIAL OR PUNITIVE DAMAGES (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT
                  OF SUBSTITUTE GOODS OR SERVICES, LOSS OF USE, DATA OR PROFITS, BUSINESS INTERRUPTION OR ANY OTHER
                  DAMAGES OR LOSSES, ARISING OUT OF OR RELATED TO YOUR USE OR INABILITY TO USE THE SERVICES,), HOWEVER
                  CAUSED AND UNDER ANY THEORY OF LIABILITY, WHETHER UNDER THESE TERMS OR OTHERWISE ARISING IN ANY WAY IN
                  CONNECTION WITH THE SERVICES, OR THESE TERMS AND WHETHER IN CONTRACT, STRICT LIABILITY OR TORT
                  (INCLUDING NEGLIGENCE OR OTHERWISE) EVEN IF THE COMPANY ENTITIES HAVE BEEN ADVISED OF THE POSSIBILITY
                  OF SUCH DAMAGE, OR (B) FOR ANY OTHER CLAIM, DEMAND OR DAMAGES WHATSOEVER RESULTING FROM OR ARISING OUT
                  OF OR IN CONNECTION WITH THESE TERMS OR THE DELIVERY, USE OR PERFORMANCE OF THE SERVICES. SOME
                  JURISDICTIONS (SUCH AS THE STATE OF NEW JERSEY) DO NOT ALLOW THE EXCLUSION OR LIMITATION OF INCIDENTAL
                  OR CONSEQUENTIAL DAMAGES, SO THE ABOVE EXCLUSION OR LIMITATION MAY NOT APPLY TO YOU. THE COMPANY
                  ENTITIES’ TOTAL LIABILITY TO YOU FOR ANY DAMAGES FINALLY AWARDED SHALL NOT EXCEED THE GREATER AMOUNT
                  OF ONE HUNDRED DOLLARS ($100.00), OR THE AMOUNT YOU PAID THE COMPANY ENTITIES, IF ANY, IN THE PAST SIX
                  (6) MONTHS FOR THE SERVICES GIVING RISE TO THE CLAIM. THE FOREGOING LIMITATIONS WILL APPLY EVEN IF THE
                  ABOVE STATED REMEDY FAILS OF ITS ESSENTIAL PURPOSE.
                </li>
                <li>
                  Indemnification. By entering into these Terms and accessing or using the Services, you agree that you
                  shall defend, indemnify and hold the Company Entities harmless from and against any and all claims,
                  costs, damages, losses, liabilities and expenses (including attorneys’ fees and costs) incurred by the
                  Company Entities arising out of or in connection with: (a) your violation or breach of any term of
                  these Terms or any applicable law or regulation; (b) your violation of any rights of any third party;
                  (c) your access to or use of the Services; or (d) your negligence or wilful misconduct.
                </li>
              </ol>
              <br />
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              9. ARBITRATION AND CLASS ACTION WAIVER
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              <ol
                css={css`
                  padding: 0;
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: ${theme.spacing(1)};
                  margin-left: ${theme.spacing(2)};
                `}
              >
                <li>
                  Informal Process First. You agree that in the event of any dispute between you and the Company
                  Entities, you will first contact the Company and make a good faith sustained effort to resolve the
                  dispute before resorting to more formal means of resolution, including without limitation, any court
                  action.
                </li>
                <li>
                  Arbitration Agreement and Class Action Waiver. After the informal dispute resolution process, any
                  remaining dispute, controversy, or claim (collectively, “Claim”) relating in any way to your use of
                  the Company’s services and/or products, including the Services, will be resolved by arbitration,
                  including threshold questions of arbitrability of the Claim. You and the Company agree that any Claim
                  will be settled by final and binding arbitration, using the English language, administered by JAMS
                  under its Comprehensive Arbitration Rules and Procedures (the “JAMS Rules”) then in effect (those
                  rules are deemed to be incorporated by reference into this section, and as of the date of these
                  Terms). Arbitration will be handled by a sole arbitrator in accordance with the JAMS Rules. Judgment
                  on the arbitration award may be entered in any court that has jurisdiction. Any arbitration under
                  these Terms will take place on an individual basis – class arbitrations and class actions are not
                  permitted. You understand that by agreeing to these Terms, you and the Company are each waiving the
                  right to trial by jury or to participate in a class action or class arbitration. Notwithstanding the
                  foregoing, you and the Company will have the right to bring an action in a court of proper
                  jurisdiction for injunctive or other equitable or conservatory relief, pending a final decision by the
                  arbitrator. You may instead assert your claim in “small claims” court, but only if your claim
                  qualifies, your claim remains in such court and your claim remains on an individual,
                  non-representative and non-class basis.
                </li>
                <li>
                  Costs of Arbitration. Payment for any and all reasonable JAMS filing, administrative and arbitrator
                  fees will be in accordance with the JAMS Rules. If the value of your claim does not exceed $10,000,
                  the Company will pay for the reasonable filing, administrative and arbitrator fees associated with the
                  arbitration, unless the arbitrator finds that either the substance of your claim or the relief sought
                  was frivolous or brought for an improper purpose.
                </li>
                <li>
                  Opt-Out. You have the right to opt-out and not be bound by the arbitration provisions set forth in
                  these Terms by sending written notice of your decision to opt-out toucan@babbel.com or to the U.S.
                  mailing address listed in the “How to Contact Us” section of these Terms. The notice must be sent to
                  the Company within thirty (30) days of your registering to use the Services or agreeing to these
                  Terms, otherwise you shall be bound to arbitrate disputes in accordance with these Terms. If you
                  opt-out of these arbitration provisions, the Company also will not be bound by them.
                </li>
              </ol>
              <br />
            </Typography>
            <Divider
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            />
            <Typography
              variant="sm"
              isBold
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(7)};
              `}
            >
              10. ADDITIONAL PROVISIONS
            </Typography>
            <Typography
              variant="sm"
              color={ToucanColors.gray[500]}
              css={css`
                margin-top: ${theme.spacing(1)};
              `}
            >
              <ol
                css={css`
                  padding: 0;
                  margin-top: ${theme.spacing(1)};
                  margin-bottom: ${theme.spacing(1)};
                  margin-left: ${theme.spacing(2)};
                `}
              >
                <li>
                  Updating These Terms. We may modify these Terms from time to time in which case we will update the
                  “Last Revised” date at the top of these Terms. If we make changes that are material, we will use
                  reasonable efforts to attempt to notify you, such as by e-mail and/or by placing a prominent notice on
                  the first page of the Website. However, it is your sole responsibility to review these Terms from time
                  to time to view any such changes. The updated Terms will be effective as of the time of posting, or
                  such later date as may be specified in the updated Terms. Your continued access or use of the Services
                  after the modifications have become effective will be deemed your acceptance of the modified Terms.
                </li>
                <li>
                  Termination of License and Your Account. If you breach any of the provisions of these Terms, all
                  licenses granted by the Company will terminate automatically. Additionally, the Company may suspend,
                  disable, or delete your Account and/or the Services (or any part of the foregoing) with or without
                  notice, for any or no reason. If the Company deletes your Account for any suspected breach of these
                  Terms by you, you are prohibited from re-registering for the Services under a different name. All
                  sections which by their nature should survive the termination of these Terms shall continue in full
                  force and effect subsequent to and notwithstanding any termination of this Agreement by the Company or
                  you. Termination will not limit any of the Company’s other rights or remedies at law or in equity.
                </li>
                <li>
                  Injunctive Relief. You agree that a breach of these Terms will cause irreparable injury to the Company
                  for which monetary damages would not be an adequate remedy and the Company shall be entitled to
                  equitable relief in addition to any remedies it may have hereunder or at law without a bond, other
                  security or proof of damages.
                </li>
                <li>
                  California Residents. If you are a California resident, in accordance with Cal. Civ. Code § 1789.3,
                  you may report complaints to the Complaint Assistance Unit of the Division of Consumer Services of the
                  California Department of Consumer Affairs by contacting them in writing at 1625 North Market Blvd.,
                  Suite N 112 Sacramento, CA 95834, or by telephone at (800) 952-5210.
                </li>
                <li>
                  U.S. Government Restricted Rights. The Services and related documentation are &quot;Commercial
                  Items&quot;, as that term is defined at 48 C.F.R. §2.101, consisting of &quot;Commercial Computer
                  Software&quot; and &quot;Commercial Computer Software Documentation&quot;, as such terms are used in
                  48 C.F.R. §12.212 or 48 C.F.R. §227.7202, as applicable. Consistent with 48 C.F.R. §12.212 or 48
                  C.F.R. §227.7202-1 through 227.7202-4, as applicable, the Commercial Computer Software and Commercial
                  Computer Software Documentation are being licensed to U.S. Government end users (a) only as Commercial
                  Items, and (b) with only those rights as are granted to all other end users pursuant to the terms and
                  conditions herein.
                </li>
                <li>
                  Export Laws. You agree that you will not export or re-export, directly or indirectly, the Services
                  and/or other information or materials provided by the Company hereunder, to any country for which the
                  United States or any other relevant jurisdiction requires any export license or other governmental
                  approval at the time of export without first obtaining such license or approval. In particular, but
                  without limitation, the Services may not be exported or re-exported (a) into any U.S. embargoed
                  countries or any country that has been designated by the U.S. Government as a “terrorist supporting”
                  country, or (b) to anyone listed on any U.S. Government list of prohibited or restricted parties,
                  including the U.S. Treasury Department&#39;s list of Specially Designated Nationals or the U.S.
                  Department of Commerce Denied Person’s List or Entity List. By using the Services, you represent and
                  warrant that you are not located in any such country or on any such list. You are responsible for and
                  hereby agree to comply at your sole expense with all applicable United States export laws and
                  regulations.
                </li>
                <li>
                  Assignment. Your contractual relationship with us and the licenses granted hereunder may be freely
                  assigned by the Company, including but not limited to in connection with a merger, acquisition,
                  consolidation, corporate reorganization, change of control or sale of all or substantially all of the
                  Company’s stock or assets. The contractual relationship and the licenses granted hereunder may not be
                  assigned by you without the prior express written consent of the Company.
                </li>
                <li>
                  Miscellaneous. If any provision of these Terms shall be unlawful, void or for any reason
                  unenforceable, then that provision shall be deemed severable from these Terms and shall not affect the
                  validity and enforceability of any remaining provisions. No waiver by either party of any breach or
                  default hereunder shall be deemed to be a waiver of any preceding or subsequent breach or default. The
                  section headings used herein are for reference only and shall not be read to have any legal effect.
                  The Services are operated by us in the United States. Those who choose to access the Services from
                  locations outside the United States do so at their own initiative and are responsible for compliance
                  with applicable local laws. These Terms are governed by the laws of the State of California, without
                  regard to conflict of laws rules, and the proper venue for any disputes arising out of or relating to
                  any of the same will be the arbitration venue set forth in Section 9, or if arbitration does not
                  apply, then the state and federal courts located in Los Angeles, California.
                </li>
                <li>
                  How to Contact Us. You may contact us regarding the Services or these Terms at: Babbel GmbH,
                  Andreasstraße 72, 10243 Berlin, Germany, or by e-mail at toucan@babbel.com.
                </li>
              </ol>
              <br />
            </Typography>
          </Box>
        </Box>
      </Layout>
    </>
  );
};

export default ExtensionTerms;

export const getServerSideProps: GetServerSideProps = async ({ locale }) => ({
  props: {
    ...(await getTranslations({
      locale,
      namespaces: 'tos',
    })),
  },
});
